import EnumUtility from '@/enums/EnumUtility';

// values are mapped to API enum, do not change
export enum CloudAuthenticationType {
  Dropbox = 1,
  OneDrive = 2,
  GoogleDrive = 4,
  Box = 8,
  Amazon = 16,
  Gmail = 32,
  MipProtection = 64,
  MipLabel = 128,
  Bitbucket = 256,
  AzureBlob = 512,
  ExchangeMail = 1024
}

export enum DataAssociationType {
  Endpoint = 0,
  Tag = 1
}

export enum DatabaseType
{
  Oracle = 0,
  SQLServer = 1,
  Sybase = 2,
  IBMDB2 = 3,
  Informix = 4,
  InterBase = 5,
  SQLBase = 6,
  SAPSQLAnywhere = 7,
  MySQL = 8,
  SQLite = 9,
  PostgreSQL = 10,
  ODBC = 11,
  OLEDB = 12,
  MongoDB = 13,
  Snowflake = 14
}

export enum EndpointTypes {
  None = 0,
  Database = 1,
  WebSite = 2,
  Exchange = 4, // On prem exchange servers
  Remote = 8,
  SharePointSite = 16,
  CloudStorage = 32,
  CloudEmail = 64, // Gmail and Exchange Online
  All = 119
}

// values are hex to match server side flags enum values
export enum AclTypes {
  None = 0x0000000000000000,
  Error = 0x0000000000000001,
  Windows = 0x0000000000000002,
  Posix = 0x0000000000000004,
  Nfs4 = 0x0000000000000008,
  AllSystems = 0x000000000000000E
}

export abstract class AclTypesUtil {
  public static toDisplay(type: AclTypes): string {
    const getString = (t: AclTypes): string => {
      if (t === undefined) {
        t = 0;
      }

      switch (t) {
      case AclTypes.Error:
        return 'Could not obtain';
      case AclTypes.Nfs4:
        return 'Nfs 4';
      case AclTypes.AllSystems:
        return null;
      default:
        return AclTypes[<number>t];
      }
    };

    return EnumUtility.flagsEnumToDisplay(type, AclTypes, getString);
  }
}

// values are hex to match server side flags enum values
export enum AceRights {
  NoAccess = 0x0000,
  ReadData = 0x0001,
  WriteData = 0x0002,
  AppendData = 0x0004,
  ReadExtendedAttributes = 0x0008,
  WriteExtendedAttributes = 0x0010,
  Execute = 0x0020,
  ReadAcl = 0x0040,
  ReadAttributes = 0x0080,
  WriteAttributes = 0x0100,
  Delete = 0x10000,
  ReadControl = 0x20000,
  WriteAcl = 0x40000,
  TakeOwnership = 0x80000,
  Synchronize = 0x100000,
  GenericRead = 0x120089,
  GenericExecute = 0x1200A0,
  GenericWrite = 0x120116,
  FullControl = 0x1F01FF
}

export abstract class AceRightsUtil {
  public static toDisplay(type: AceRights): string {
    const getString = (t: AceRights): string => {
      if (t === undefined) {
        t = 0;
      }

      switch (t) {
        case AceRights.NoAccess:
          return 'No Access';
        case AceRights.ReadData:
          return 'Read Data';
        case AceRights.WriteData:
          return 'Write Data';
        case AceRights.AppendData:
          return 'Append Data';
        case AceRights.ReadExtendedAttributes:
          return 'Read Extended Attributes';
        case AceRights.WriteExtendedAttributes:
          return 'Write Extended Attributes';
        case AceRights.ReadAcl:
          return 'Read Acl';
        case AceRights.ReadAttributes:
          return 'Read Attributes';
        case AceRights.WriteAttributes:
          return 'Write Attributes';
        case AceRights.ReadControl:
          return 'Read Control';
        case AceRights.WriteAcl:
          return 'Write Acl';
        case AceRights.TakeOwnership:
          return 'Take Ownership';
        case AceRights.GenericRead:
          return 'Generic Read';
        case AceRights.GenericExecute:
          return 'Generic Execute';
        case AceRights.GenericWrite:
          return 'Generic Write';
        case AceRights.FullControl:
          return 'Full Control';
        default:
          return AceRights[t];
      }
    };

    return EnumUtility.flagsEnumToDisplay(type, AceRights, getString, AceRights.FullControl);
  }

  public static aclTypes(type: AceRights): AclTypes {
    if (type === undefined || type === null) {
      return null;
    }

    switch (type) {
      case AceRights.ReadAcl:
        return AclTypes.Nfs4;
      case AceRights.ReadControl:
        return AclTypes.Windows;
      case AceRights.ReadData:
      case AceRights.WriteData:
      case AceRights.AppendData:
      case AceRights.ReadExtendedAttributes:
      case AceRights.WriteExtendedAttributes:
      case AceRights.Execute:
      case AceRights.ReadAttributes:
      case AceRights.WriteAttributes:
      case AceRights.Delete:
      case AceRights.WriteAcl:
      case AceRights.TakeOwnership:
      case AceRights.Synchronize:
        // eslint-disable-next-line
        return AclTypes.Windows | AclTypes.Nfs4;
      case AceRights.NoAccess:
      case AceRights.GenericRead:
      case AceRights.GenericWrite:
      case AceRights.GenericExecute:
      case AceRights.FullControl:
        return AclTypes.AllSystems;
      default:
        return AclTypes.None;
    }
  }
}

export enum AceType {
  Allowed = 0,
  Denied = 1,
  SystemAudit = 2,
  SystemAlarm = 3
}

export abstract class AceTypeUtil {
  public static toDisplay(type: AceType): string {
    if (type === undefined || type === null) {
      type = 0;
    }

    switch (type) {
    case AceType.Allowed:
      return 'Allow';
    case AceType.Denied:
      return 'Deny';
    case AceType.SystemAudit:
      return 'System Audit';
    case AceType.SystemAlarm:
      return 'System Alarm';
    default:
      return AceType[<number>type];
    }
  }
}

// values are hex to match server side enum values
export enum AceFlags {
  None = 0x0000000000000000,
  ObjectInheritAce = 0x0000000000000001,
  ContainerInheritAce = 0x0000000000000002,
  NoPropagateInheritAce = 0x0000000000000004,
  InheritOnlyAce = 0x0000000000000008,
  InheritedAce = 0x0000000000000010,
  SuccessfulAccess = 0x0000000000000040,
  FailedAccess = 0x0000000000000080
}

export abstract class AceFlagsUtil {
  public static toDisplay(type: AceFlags): string {
    const getString = (t: AceFlags): string => {
      if (t === undefined || t === null) {
        t = 0;
      }

      switch (t) {
      case AceFlags.ObjectInheritAce:
        return 'Object Inherit Ace';
      case AceFlags.ContainerInheritAce:
        return 'Container Inherit Ace';
      case AceFlags.NoPropagateInheritAce:
        return 'No Propagate Inherit Ace';
      case AceFlags.InheritOnlyAce:
        return 'Inherit Only Ace';
      case AceFlags.InheritedAce:
        return 'Inherited Ace';
      case AceFlags.SuccessfulAccess:
        return 'Successful Access';
      case AceFlags.FailedAccess:
        return 'Failed Access';
      default:
        return AceFlags[<number>t];
      }
    };

    return EnumUtility.flagsEnumToDisplay(type, AceFlags, getString);
  }
}

export enum AnyFindCode {
  SSN = 10001,
  CCN,
  Password,
  ACCT,
  DL,
  DOB,
  PHONE,
  EMAIL,
  ADDRESS,
  PASSPORT,
  CANADASIN = 10012,
  UKNINO,
  UKNHS,
  AUTFN,
  HEALTHINFO = 10022,
}

// values are mapped to database key, do not change
export enum GridConfigurationKey {
  ScanResultsGrid,
  DataWatcherResultsGrid,
  ScanResultsGridByLocation,
  DataWatcherResultsGridByLocation
}

export enum IncludeExcludeOption {
  Include,
  Exclude
}

// flags enum mapped to server enum
export enum PolicyAssociationsMode {
  None = 0,
  SearchAgent = 1,
  DiscoveryAgent = 2,
  DiscoveryQueue = 4
}
